import { useEffect, useRef, useState } from "react";
import { useAdminToast } from "../../context/AdminToastContext";
import { parseApiError } from "../../lib/http/apiErrors";
import { formatISTDateTime } from "../../utils/dateUtils";
import {
  fetchInvoiceLogo,
  resetInvoiceLogo,
  uploadInvoiceLogo,
  type InvoiceLogo,
} from "../../features/admin/billingApi";

/** Upload the logo printed on invoices and receipts ({{ brand.logo }}).
 *  Stored by the backend (no hosting needed); PNG/JPG up to 1 MB, resized
 *  to fit 256×256. */
export default function InvoiceLogoCard({ onChanged }: { onChanged?: () => void }) {
  const { showError, showSuccess } = useAdminToast();
  const [logo, setLogo] = useState<InvoiceLogo | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchInvoiceLogo().then(setLogo).catch((e) => showError(parseApiError(e).message));
  }, [showError]);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      showError("Logo must be a PNG or JPG");
      return;
    }
    if (file.size > 1024 * 1024) {
      showError("Logo must be at most 1 MB");
      return;
    }
    setBusy(true);
    try {
      setLogo(await uploadInvoiceLogo(file));
      showSuccess("Logo updated — new PDFs use it straight away");
      onChanged?.();
    } catch (e) {
      showError(parseApiError(e).message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onReset = async () => {
    if (!window.confirm("Go back to the built-in Gullivr logo?")) return;
    setBusy(true);
    try {
      setLogo(await resetInvoiceLogo());
      showSuccess("Back to the Gullivr logo");
      onChanged?.();
    } catch (e) {
      showError(parseApiError(e).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 rounded-2xl border border-gray-800 bg-gray-900/60 p-5">
      <div className="flex flex-wrap items-center gap-5">
        <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-gray-700 bg-white p-1">
          {logo?.data_uri ? (
            <img src={logo.data_uri} alt="Invoice logo" className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-xs text-gray-400">…</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-white">Logo on invoices &amp; receipts</h3>
          <p className="mt-1 text-xs text-gray-500">
            {logo?.custom
              ? `Uploaded${logo.uploaded_by ? ` by ${logo.uploaded_by}` : ""}${logo.uploaded_at ? ` on ${formatISTDateTime(logo.uploaded_at)}` : ""}.`
              : "Using the built-in Gullivr logo."}{" "}
            PNG or JPG, up to 1 MB — it is resized automatically. Appears wherever the design uses{" "}
            <code>{"{{ brand.logo }}"}</code> (the built-in design already does).
          </p>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden"
            onChange={(e) => void onFile(e.target.files?.[0])} />
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
            className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50">
            {busy ? "Working…" : logo?.custom ? "Replace logo" : "Upload logo"}
          </button>
          {logo?.custom && (
            <button type="button" disabled={busy} onClick={() => void onReset()}
              className="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-300 hover:text-white disabled:opacity-50">
              Reset to Gullivr logo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
